<template>
  <div v-if="shouldShowBlock" class="additional-recipients-standalone">
    <div class="cc-title">Add Additional Recipients</div>
    <div v-if="showDescription" class="cc-desc">
      Select additional Valuation Team members to include in CC when forwarding. By
      default, this email will be sent to the Primary Valuer and saved to the
      Correspondence folder in the Workfile.
    </div>

    <div v-if="hasValidAssignment" class="cc-list">
      <div class="cc-row-flex" v-for="option in ccOptions" :key="option.label">
        <v-checkbox
          v-model="checkedCC[option.label]"
          :id="option.label"
          hide-details
          dense
          color="#003f2d"
          class="cc-checkbox-flex"
        />
        <span class="cc-label-flex">{{ option.label }}</span>
        <span class="cc-names-flex">{{ option.names.join(", ") }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { fetchAllocationData } from "../../service/engagement.js";

export default {
  name: "AdditionalRecipientsStandalone",
  props: {
    showDescription: {
      type: Boolean,
      default: false
    },
    ccOptionsConfig: {
      type: Object,
      default: () => ({
        includeOriginator: true,
        includeValuers: true,
        includeValuationTeam: true,
        includePrimaryValuer: false
      })
    },
    assignment: {
      type: Object,
      default: null
    },
    context: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'clientFollowUp', 'propertyFollowUp'].includes(value)
    }
  },
  data() {
    return {
      checkedCC: {},
      allocationData: null
    };
  },
  mounted() {
    this.loadAllocationData();
  },
  computed: {
    ...mapGetters({
      storeSelectedAssignment: "assignments/selectedAssignment",
      clientFollowUpSelectedAssignment: "assignments/selectedAssignmentClientFollowUp"
    }),
    selectedAssignment() {
      if (this.context === 'clientFollowUp') {
        return this.clientFollowUpSelectedAssignment;
      } else if (this.context === 'propertyFollowUp') {
        // For propertyFollowUp, only use the assignment prop (no store fallback)
        return this.assignment;
      } else {
        return this.assignment || this.storeSelectedAssignment;
      }
    },
    shouldShowBlock() {
      // For propertyFollowUp context, only show when there's a valid assignment
      if (this.context === 'propertyFollowUp') {
        return this.selectedAssignment && this.selectedAssignment.viewEventId;
      }
      // For other contexts, only show when there's a valid assignment
      return this.selectedAssignment && this.selectedAssignment.viewEventId;
    },
    hasValidAssignment() {
      return this.selectedAssignment && this.selectedAssignment.viewEventId;
    },
    ccOptions() {
      // Return empty array if no assignment selected
      if (!this.selectedAssignment || !this.selectedAssignment.viewEventId) {
        return [];
      }
      // Use provided list if available, otherwise calculate internally
      return this.ccOptionsList || this.calculateCcOptions();
    },
    selectedCCEmails() {
      const selectedEmails = [];
      const primaryValuerEmail = this.selectedAssignment?.primaryValuerEmail?.toLowerCase();
      this.ccOptions.forEach(option => {
        if (this.checkedCC[option.label] && option.email) {
          option.email.forEach(email => {
             selectedEmails.push(email);
          });
          
          // Add primary valuer email if Valuation Team is selected
          if (option.label === 'Valuation Team' && primaryValuerEmail) {
            selectedEmails.push(primaryValuerEmail);
          }
        }
      });
      
      // Deduplicate emails case-insensitively
      const seenEmails = new Set();
      const uniqueEmails = selectedEmails.filter((email) => {
        if (typeof email !== "string") return false;
        
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || seenEmails.has(normalizedEmail)) return false;
        
        seenEmails.add(normalizedEmail);
        return true;
      });
      
      console.log('Selected CC emails:', uniqueEmails);
      return uniqueEmails;
    }
  },
  watch: {
    assignment: {
      immediate: true,
      handler(newAssignment) {
        if (!newAssignment) {
          // Immediately clear state when assignment prop is null
          this.resetState();
        }
      }
    },
    ccOptions: {
      immediate: true,
      handler(newOptions) {
        if (!newOptions || newOptions.length === 0) {
          this.checkedCC = {};
          return;
        }
        const checked = {};
        newOptions.forEach(opt => {
          // Preserve existing checkbox state if it exists, otherwise default to false
          checked[opt.label] = this.checkedCC[opt.label] !== undefined
            ? this.checkedCC[opt.label]
            : false;
        });
        this.checkedCC = checked;
      }
    },
    selectedAssignment: {
      handler(newAssignment, oldAssignment) {
        if (newAssignment && newAssignment.viewEventId &&
            (!oldAssignment || newAssignment.viewEventId !== oldAssignment.viewEventId)) {
          this.loadAllocationData();
        } else if (!newAssignment) {
          // Clear state when assignment is deselected or null
          this.resetState();
        }
      },
      immediate: false
    },
    'checkedCC.Valuation Team': {
      handler(isSelected) {
        if (isSelected) {
          this.checkedCC.Originator = true;
          this.checkedCC['Valuer(s)'] = true;
          this.checkedCC = { ...this.checkedCC };
        }
      }
    },
    checkedCC: {
      deep: true,
      handler() {
        this.$emit('selection-changed', {
          checkedCC: this.checkedCC,
          selectedEmails: this.selectedCCEmails
        });
      }
    }
  },
  methods: {
    calculateCcOptions() {
      const a = this.selectedAssignment;
      const valuationTeamEntities = this.allocationData ? this.allocationData.appraiserAllocationBillingModel : [];
      if (!a) return [];

      const options = [];

      if (this.ccOptionsConfig.includeOriginator) {
        options.push({
          label: "Originator",
          checked: false,
          names: a.originator ? [a.originator] : [],
          email: a.originatorEmail ? [a.originatorEmail] : []
        });
      }

      if (this.ccOptionsConfig.includeValuers) {
        options.push({
          label: "Valuer(s)",
          checked: false,
          names: a.valuers ? a.valuers.map(v => v.fullName) : [],
          email: a.valuers ? a.valuers.map(v => v.emailAddress) : []
        });
      }

      if (this.ccOptionsConfig.includeValuationTeam) {
        options.push({
          label: "Valuation Team",
          checked: false,
          names: [],
          email: valuationTeamEntities.map(v => v.email)
        });
      }

      return options;
    },
    async loadAllocationData() {
      try {
        if (this.selectedAssignment?.viewEventId) {
          this.allocationData = await fetchAllocationData(this.selectedAssignment.viewEventId);
        }
      } catch (error) {
        console.error('Failed to load allocation data:', error);
        this.allocationData = null;
      }
    },
    getSelectedEmails() {
      return this.selectedCCEmails;
    },
    getCheckedCC() {
      return this.checkedCC;
    },
    resetState() {
      // Clear all state completely
      this.checkedCC = {};
      this.allocationData = null;

      // Emit empty selection immediately
      this.$emit('selection-changed', {
        checkedCC: {},
        selectedEmails: []
      });
    }
  }
};
</script>

<style scoped>
.additional-recipients-standalone {
  width: 100%;
}

.cc-title {
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 16px;
  vertical-align: middle;
  color: #000;
}

.cc-desc {
  color: #1a1a1aa6;
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  vertical-align: middle;
}

.cc-list {
  margin-top: 5px;
  margin-left: 5px;
}

.cc-row-flex {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  min-height: 28px;
  line-height: 1;
}

.cc-checkbox-flex {
  margin-right: 8px;
  min-width: 32px;
  margin-top: 0;
  padding: 0 !important;
  height: auto;
  display: flex;
  align-items: center;
}

::v-deep .cc-checkbox-flex .v-input--selection-controls__input {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
  display: flex;
  align-items: center;
}

::v-deep .cc-checkbox-flex .v-icon,
::v-deep .cc-checkbox-flex .v-icon__svg,
::v-deep .cc-checkbox-flex svg {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  vertical-align: middle !important;
  display: inline-flex !important;
}

.cc-label-flex {
  font-size: 14px;
  margin-right: 24px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  color: #1a1a1a;
}

.cc-names-flex {
  width: 100%;
  text-align: right;
  font-size: 14px;
  color: #1a1a1a;
  line-height: 1;
  word-break: break-word;
  white-space: normal;
  display: block;
}

.cc-empty-state {
  padding: 16px;
  text-align: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-top: 8px;
}

.cc-empty-text {
  font-size: 14px;
  color: #666;
  font-style: italic;
}
</style>
